/**
 * API service for making HTTP requests to the backend.
 * Configures axios with base URL, headers, and interceptors.
 */
import axios from 'axios';

const resolveApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;

  if (!envUrl) {
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      return '/api';
    }
    return 'http://localhost:8000/api';
  }

  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';

    if (!isLocalhost) {
      try {
        const parsed = new URL(envUrl);
        const isAzureWebAppHost = parsed.hostname.endsWith('azurewebsites.net');
        const isCrossHost = parsed.hostname !== currentHost;

        if (isAzureWebAppHost && isCrossHost) {
          return '/api';
        }
      } catch {
        if (envUrl.startsWith('/')) {
          return envUrl;
        }
      }
    }
  }

  return envUrl;
};

const generateCorrelationId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const getClientErrorReportUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return '/api/auth/client-error';
    }
  }

  const envUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
  return `${envUrl.replace(/\/$/, '')}/auth/client-error`;
};

const reportClientSideNetworkError = (error) => {
  try {
    if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError' || error?.name === 'CanceledError') {
      return;
    }

    const correlationId =
      error?.correlationId ||
      error?.config?.headers?.['X-Correlation-ID'] ||
      null;

    const authHeader = error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
    const endpointPath = error?.config?.url || 'unknown';

    const headers = {
      'Content-Type': 'application/json',
    };

    if (authHeader) {
      headers['Authorization'] = authHeader;
    }

    fetch(getClientErrorReportUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        path: endpointPath,
        error_type: 'client_network_blocked',
        message: error?.message || 'Network error in browser before API response',
        correlation_id: correlationId,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        page_url: typeof window !== 'undefined' ? window.location.href : null,
      }),
      keepalive: true,
    }).catch(() => {
      // no-op - best effort telemetry
    });
  } catch {
    // no-op - best effort telemetry
  }
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
});

// Request interceptor - add auth token and dev user header
apiClient.interceptors.request.use(
  async (config) => {
    try {
      config.headers['X-Correlation-ID'] = config.headers['X-Correlation-ID'] || generateCorrelationId();

      // Local development with bypass
      if (import.meta.env.VITE_LOCAL_AUTH === 'true') {
        config.headers['X-Dev-User'] = localStorage.getItem('devUser') || 'testuser';
        console.log('Using development authentication');
        return config;
      }

      // Production/Deployed: Get Azure AD token from MSAL
      // Try to get token from AuthContext via window (set by AuthContext)
      if (window.__getAccessToken) {
        try {
          const token = await window.__getAccessToken();
          if (token && token !== 'dev-mock-token') {
            config.headers['Authorization'] = `Bearer ${token}`;
            console.log('Added Azure AD token to request');
          }
        } catch (tokenError) {
          console.error('Error getting access token:', tokenError);
        }
      } else {
        console.warn('No authentication method available');
      }
    } catch (err) {
      console.error('Error in request interceptor:', err);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const responseCorrelationId = error.response?.headers?.['x-correlation-id'];
    const requestCorrelationId = error.config?.headers?.['X-Correlation-ID'];
    error.correlationId = responseCorrelationId || requestCorrelationId || null;

    console.error('API Error:', error.response?.data || error.message);
    if (error.correlationId) {
      console.error('Correlation ID:', error.correlationId);
    }

    if (!error.response && (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || ''))) {
      reportClientSideNetworkError(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Preferred Ship-From API
 */
export const shipFromAPI = {
  /**
   * Get paginated forecast items with filters
   * @param {Object} params - Query parameters (page, filters, etc.)
   * @param {AbortSignal} signal - Optional abort signal to cancel request
   */
  getItems: async (params, signal) => {
    const response = await apiClient.get('/ship-from/items', { 
      params,
      signal // Pass abort signal to axios
    });
    return response.data;
  },

  /**
   * Get available plant/mode options for a 3-piece item
   */
  getPlantModeOptions: async (material, soldTo, shipTo) => {
    const response = await apiClient.get('/ship-from/plant-mode-options', {
      params: { material, sold_to: soldTo, ship_to: shipTo },
    });
    return response.data;
  },

  /**
   * Update preferred ship-from for a single item
   */
  updateItem: async (updateData) => {
    const response = await apiClient.put('/ship-from/update', updateData);
    return response.data;
  },

  /**
   * Bulk update preferred ship-from for multiple items
   */
  bulkUpdate: async (updates) => {
    const response = await apiClient.put('/ship-from/bulk-update', { updates });
    return response.data;
  },

  /**
   * Get filter options (dynamic based on applied filters and show_reviewed)
   */
  getFilterOptions: async (params = {}) => {
    const response = await apiClient.get('/ship-from/filter-options', { params });
    return response.data;
  },

  /**
   * Get plant options for a material
   */
  getPlantOptions: async (material) => {
    const response = await apiClient.get(`/ship-from/plant-options/${encodeURIComponent(material)}`);
    return response.data;
  },

  /**
   * Get transport mode options
   */
  getTransportModes: async () => {
    const response = await apiClient.get('/ship-from/transport-modes');
    return response.data;
  },

  /**
   * Request new plant/mode combination (now writes directly to writeback table)
   */
  requestNewCombination: async (requestData) => {
    console.log('API call payload:', {
      Material: requestData.material,
      ShipTo: requestData.shipTo,
      ForecastItemID: requestData.forecastItemId || '',
      OverridePrefShipFrom: requestData.plant,
      OverridePrefMode: requestData.mode,
    });
    // Backend expects PreferredShipFromUpdate format
    const response = await apiClient.post('/ship-from/request-new-combination', {
      Material: requestData.material,
      ShipTo: requestData.shipTo,
      ForecastItemID: requestData.forecastItemId || '',
      OverridePrefShipFrom: requestData.plant,
      OverridePrefMode: requestData.mode,
    });
    return response.data;
  },

  /**
   * Export filtered items to Excel
   */
  exportToExcel: async (filters = {}) => {
    const response = await apiClient.get('/ship-from/export', {
      params: filters,
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  },
};

/**
 * Material Review API
 */
export const materialReviewAPI = {
  /**
   * Get paginated material/plant records with filters
   * @param {Object} params - Query parameters (page, filters, etc.)
   * @param {AbortSignal} signal - Optional abort signal to cancel request
   */
  getRecords: async (params, signal) => {
    const response = await apiClient.get('/material-review/records', { 
      params,
      signal // Pass abort signal to axios
    });
    return response.data;
  },

  /**
   * Update a single material/plant record
   */
  updateRecord: async (updateData) => {
    const response = await apiClient.put('/material-review/update', updateData);
    return response.data;
  },

  /**
   * Bulk update multiple records
   */
  bulkUpdate: async (updates) => {
    const response = await apiClient.put('/material-review/bulk-update', { updates });
    return response.data;
  },

  /**
   * Mark records as reviewed
   */
  markAsReviewed: async (recordIds) => {
    const response = await apiClient.post('/material-review/mark-reviewed', {
      MaterialPlantRecordIDs: recordIds,
    });
    return response.data;
  },

  /**
   * Unmark reviewed records
   */
  unmarkReviewed: async (recordIds) => {
    const response = await apiClient.post('/material-review/unmark-reviewed', {
      MaterialPlantRecordIDs: recordIds,
    });
    return response.data;
  },

  /**
   * Get lookup tables for dropdowns
   */
  getLookupTables: async () => {
    const response = await apiClient.get('/material-review/lookup-tables');
    return response.data;
  },

  /**
   * Get filter options
   */
  getFilterOptions: async (appliedFilters = {}) => {
    const response = await apiClient.get('/material-review/filter-options', { params: appliedFilters });
    return response.data;
  },

  /**
   * Export filtered records to Excel
   */
  exportToExcel: async (filters = {}) => {
    const response = await apiClient.get('/material-review/export', {
      params: filters,
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  },
};

/**
 * Admin Dashboard API
 */
export const adminAPI = {
  /**
   * Get dashboard summary statistics
   */
  getSummary: async () => {
    const response = await apiClient.get('/admin/summary');
    return response.data;
  },

  /**
   * Get user activity metrics
   */
  getUserActivity: async () => {
    const response = await apiClient.get('/admin/user-activity');
    return response.data;
  },

  /**
   * Get late records
   */
  getLateRecords: async (params) => {
    const response = await apiClient.get('/admin/late-records', { params });
    return response.data;
  },

  /**
   * Get review completion metrics
   */
  getReviewCompletion: async (days = 30) => {
    const response = await apiClient.get('/admin/review-completion', { params: { days } });
    return response.data;
  },

  /**
   * Get subordinate metrics (for managers)
   */
  getSubordinateMetrics: async () => {
    const response = await apiClient.get('/admin/subordinate-metrics');
    return response.data;
  },

  /**
   * Get backend error telemetry metrics (developer monitoring)
   */
  getErrorMetrics: async () => {
    const response = await apiClient.get('/admin/error-metrics');
    return response.data;
  },
};

/**
 * Authentication API
 */
export const authAPI = {
  /**
   * Get current user information
   */
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await apiClient.get('/auth/health');
    return response.data;
  },
};

export default apiClient;
